/**
 * Countdown utility for calculating time until next day
 * Uses user's local timezone so the puzzle rolls at midnight in their location
 */

/**
 * Format a Date as YYYY-MM-DD using local date components (avoids UTC pitfalls)
 * @param {Date} d
 * @returns {string}
 */
function toLocalDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Get the day key N days before a given day key (in user's local timezone)
 * @param {string} dayKey - Day key in format YYYY-MM-DD
 * @param {number} daysBack - Number of days to go back (default 1)
 * @returns {string} Day key in format YYYY-MM-DD
 */
export function getDayKeyBefore(dayKey, daysBack = 1) {
  const [y, m, day] = dayKey.split('-').map(Number);
  const d = new Date(y, m - 1, day); // local date
  d.setDate(d.getDate() - daysBack);
  return toLocalDateKey(d);
}

/**
 * Get the current day key in the user's local timezone
 * @returns {string} Day key in format YYYY-MM-DD
 */
export function getCurrentDayKey() {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(new Date());
}

/**
 * Get the previous day key (yesterday) in the user's local timezone
 * @returns {string} Day key in format YYYY-MM-DD
 */
export function getYesterdayDayKey() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toLocalDateKey(d);
}

/**
 * Get the past N days (including today)
 * @param {number} days - Number of days to get (default: 7)
 * @returns {Array<string>} Array of day keys in format YYYY-MM-DD, most recent first
 */
export function getPastDays(days = 7) {
  const dayKeys = [];
  const d = new Date();
  for (let i = 0; i < days; i++) {
    const copy = new Date(d);
    copy.setDate(copy.getDate() - i);
    dayKeys.push(toLocalDateKey(copy));
  }
  return dayKeys;
}

/**
 * Get the next day key (tomorrow) in the user's local timezone
 * @returns {string} Day key in format YYYY-MM-DD
 */
export function getNextDayKey() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return toLocalDateKey(d);
}

/**
 * Get the time until next day (midnight in user's local timezone)
 * @returns {Object} Object with hours, minutes, seconds until next day
 */
export function getTimeUntilNextDay() {
  const now = new Date();
  const hour = now.getHours();
  const minute = now.getMinutes();
  const second = now.getSeconds();

  const totalSecondsInDay = 24 * 60 * 60;
  const currentSecondsInDay = hour * 3600 + minute * 60 + second;
  const secondsUntilMidnight = totalSecondsInDay - currentSecondsInDay;

  if (secondsUntilMidnight <= 0) {
    return { hours: 0, minutes: 0, seconds: 0 };
  }

  const hours = Math.floor(secondsUntilMidnight / 3600);
  const minutes = Math.floor((secondsUntilMidnight % 3600) / 60);
  const seconds = secondsUntilMidnight % 60;

  return { hours, minutes, seconds };
}

/**
 * Format countdown time as a string
 * @param {Object} timeObj - Object with hours, minutes, seconds
 * @returns {string} Formatted string like "5h 23m 45s"
 */
export function formatCountdown(timeObj) {
  const { hours, minutes, seconds } = timeObj;
  const parts = [];
  
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes}m`);
  }
  parts.push(`${seconds}s`);
  
  return parts.join(' ');
}
